import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  FormControlLabel
} from '@mui/material';
import { useData } from '../../context/DataContext';
import apiClient from '../../services/api';
import { v4 as uuidv4 } from 'uuid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';

const ManagePosts = () => {
  const { state, dispatch } = useData();
  const { posts } = state;

  const [openDialog, setOpenDialog] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    author: '',
    date: new Date().toISOString().split('T')[0],
    time: '',
    showDate: true,
    showTime: false,
  });
  const [imageFile, setImageFile] = useState(null);

  const handleOpenDialog = (post = null) => {
    if (post) {
      setEditingPost(post);
      setFormData({
        title: post.title,
        content: post.content,
        author: post.author,
        date: post.date || new Date().toISOString().split('T')[0],
        time: post.time || '',
        showDate: post.showDate !== false,
        showTime: !!post.showTime,
      });
      setImageFile(null);
    } else {
      setEditingPost(null);
      setFormData({
        title: '',
        content: '',
        author: '',
        date: new Date().toISOString().split('T')[0],
        time: '',
        showDate: true,
        showTime: false,
      });
      setImageFile(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPost(null);
    setFormData({
      title: '',
      content: '',
      author: '',
      image: ''
    });
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    setImageFile(file);
  };

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setFormData({ ...formData, [name]: checked });
  };

  const handleSubmit = async () => {
    if (editingPost) {
      // Update existing post; support image upload if a new file is chosen
      if (imageFile) {
        await apiClient.updatePostWithImage({ ...editingPost, ...formData }, imageFile);
        const result = await apiClient.getPosts();
        if (result && result.success) {
          await dispatch({ type: 'LOAD_POSTS', payload: result.data });
        }
      } else {
        await dispatch({
          type: 'UPDATE_POST',
          payload: { ...editingPost, ...formData }
        });
      }
    } else {
      // Create new post; prefer BLOB upload if an image is provided
      const newPost = {
        id: uuidv4(),
        ...formData,
        date: new Date().toISOString().split('T')[0]
      };
      if (imageFile) {
        await apiClient.addPostWithImage(newPost, imageFile);
        // Reload posts after creating with image to reflect imageId/image URL
        const result = await apiClient.getPosts();
        if (result && result.success) {
          await dispatch({ type: 'LOAD_POSTS', payload: result.data });
        }
      } else {
        await dispatch({ type: 'ADD_POST', payload: newPost });
      }
    }
    handleCloseDialog();
  };

  const handleDelete = async (postId) => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      await dispatch({
        type: 'DELETE_POST',
        payload: postId
      });
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Manage Posts
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add New Post
        </Button>
      </Box>

      {/* Posts Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Author</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {posts.map((post) => (
              <TableRow key={post.id}>
                <TableCell>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    {post.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {post.content.substring(0, 100)}...
                  </Typography>
                </TableCell>
                <TableCell>{post.author}</TableCell>
                <TableCell>{new Date(post.date).toLocaleDateString()}</TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDialog(post)}
                    color="primary"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(post.id)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingPost ? 'Edit Post' : 'Add New Post'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Post Title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Author"
                name="author"
                value={formData.author}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="date"
                label="Date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="time"
                label="Time"
                name="time"
                value={formData.time}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={<Switch name="showDate" checked={formData.showDate} onChange={handleSwitchChange} />}
                label="Show Date"
              />
              <FormControlLabel
                control={<Switch name="showTime" checked={formData.showTime} onChange={handleSwitchChange} />}
                label="Show Time"
              />
            </Grid>
            <Grid item xs={12}>
              <Button variant="outlined" component="label">
                {imageFile ? 'Change Image' : 'Upload Image'}
                <input hidden accept="image/*" type="file" onChange={handleImageChange} />
              </Button>
              {imageFile && (
                <Typography variant="caption" sx={{ ml: 2 }}>
                  {imageFile.name}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Content"
                name="content"
                value={formData.content}
                onChange={handleInputChange}
                multiline
                rows={6}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingPost ? 'Update Post' : 'Add Post'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManagePosts; 